import React, { useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';

const ORDERS_KEY = 'sasta_bazaar_orders';

const steps = [
    'Cart',
    'Delivery Address',
    'Order Summary',
    'Success'
];

const Checkout = () => {
    const { cartItems, setCartItems } = useCart();
    const [step, setStep] = useState(0);
    const [shipping, setShipping] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
    });
    const [order, setOrder] = useState(null);
    const navigate = useNavigate();

    const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    const handleChange = (e) => {
        setShipping({ ...shipping, [e.target.name]: e.target.value });
    };

    const nextStep = () => setStep((prev) => prev + 1);
    const prevStep = () => setStep((prev) => prev - 1);

    // Step 0: Cart Review
    if (step === 0) {
        if (cartItems.length === 0) {
            return (
                <div className="max-w-lg mx-auto mt-12 text-center bg-white rounded-lg shadow p-8">
                    <h2 className="text-2xl font-bold mb-3">Your cart is empty</h2>
                    <button
                        className="bg-orange-500 text-white px-6 py-2 rounded mt-4"
                        onClick={() => navigate('/')}
                    >
                        Shop Products
                    </button>
                </div>
            );
        }
        return (
            <div className="max-w-xl mx-auto mt-8 bg-white rounded-lg shadow p-8">
                <Stepper step={step} />
                <h2 className="text-xl font-bold mb-6">Cart Review</h2>
                <ul>
                    {cartItems.map(item => (
                        <li key={item.id} className="mb-2 flex items-center">
                            <img src={item.image} alt={item.name} className="w-10 h-10 mr-3 rounded object-contain border" />
                            <span>{item.name}</span>
                            <span className="ml-auto font-semibold">₹{item.price} × {item.quantity}</span>
                        </li>
                    ))}
                </ul>
                <div className="text-right font-bold my-4">Total: ₹{total}</div>
                <div className="flex justify-end gap-2">
                    <button className="bg-orange-500 text-white px-6 py-2 rounded" onClick={nextStep}>
                        Continue to Address
                    </button>
                </div>
            </div>
        );
    }

    // Step 1: Delivery Address
    if (step === 1) {
        return (
            <div className="max-w-xl mx-auto mt-8 bg-white rounded-lg shadow p-8">
                <Stepper step={step} />
                <h2 className="text-xl font-bold mb-6">Delivery Address</h2>
                <form onSubmit={e => { e.preventDefault(); nextStep(); }}>
                    <input name="name" required value={shipping.name} onChange={handleChange}
                           placeholder="Full Name"
                           className="w-full px-4 py-2 border rounded focus:outline-none mb-3"
                    />
                    <input name="address" required value={shipping.address} onChange={handleChange}
                           placeholder="Address"
                           className="w-full px-4 py-2 border rounded focus:outline-none mb-3"
                    />
                    <input name="phone" required value={shipping.phone} onChange={handleChange}
                           placeholder="Phone"
                           type="tel"
                           className="w-full px-4 py-2 border rounded focus:outline-none mb-3"
                    />
                    <input name="email" required value={shipping.email} onChange={handleChange}
                           placeholder="Email"
                           type="email"
                           className="w-full px-4 py-2 border rounded focus:outline-none mb-3"
                    />
                    <div className="flex justify-between mt-6">
                        <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={prevStep}>Back</button>
                        <button type="submit" className="px-6 py-2 bg-orange-500 text-white rounded">Continue</button>
                    </div>
                </form>
            </div>
        );
    }

    // Step 2: Order Summary & Place Order
    if (step === 2) {
        return (
            <div className="max-w-xl mx-auto mt-8 bg-white rounded-lg shadow p-8">
                <Stepper step={step} />
                <h2 className="text-xl font-bold mb-6">Order Summary</h2>
                <div className="mb-3">
                    <div className="font-semibold">Delivery To:</div>
                    <div>{shipping.name}, {shipping.address}, {shipping.phone}, {shipping.email}</div>
                </div>
                <ul>
                    {cartItems.map(item => (
                        <li key={item.id} className="mb-2 flex items-center">
                            <img src={item.image} alt={item.name} className="w-10 h-10 mr-3 rounded object-contain border" />
                            <span>{item.name}</span>
                            <span className="ml-auto font-semibold">₹{item.price} × {item.quantity}</span>
                        </li>
                    ))}
                </ul>
                <div className="text-right font-bold my-4">Total: ₹{total}</div>
                <div className="flex justify-between mt-6">
                    <button className="px-4 py-2 bg-gray-200 rounded" onClick={prevStep}>Back</button>
                    <button
                        className="px-6 py-2 bg-green-600 text-white rounded"
                        onClick={() => {
                            // Save order
                            const order = {
                                id: Date.now(),
                                items: cartItems,
                                shipping,
                                total,
                                date: new Date().toLocaleString(),
                            };
                            const prevOrders = JSON.parse(localStorage.getItem(ORDERS_KEY) || '[]');
                            localStorage.setItem(ORDERS_KEY, JSON.stringify([...prevOrders, order]));
                            setCartItems([]);
                            setOrder(order);
                            nextStep();
                        }}
                    >
                        Place Order
                    </button>
                </div>
            </div>
        );
    }

    // Step 3: Success/Confirmation
    if (step === 3) {
        return (
            <div className="max-w-lg mx-auto mt-12 text-center bg-white rounded-lg shadow p-8">
                <Stepper step={step} />
                <h2 className="text-2xl font-bold mb-3">🎉 Order Placed!</h2>
                <p className="text-gray-700 mb-4">
                    Thank you <span className="font-semibold">{order?.shipping.name}</span>!<br />
                    Your order has been placed.
                </p>
                <div className="text-left bg-gray-50 rounded p-4 mb-4">
                    <div className="font-semibold mb-2">Order Summary</div>
                    <ul className="mb-2 text-sm">
                        {order?.items.map(item => (
                            <li key={item.id}>
                                <span>{item.name} × {item.quantity}</span> = ₹{item.quantity * item.price}
                            </li>
                        ))}
                    </ul>
                    <div className="font-bold text-right">Total: ₹{order?.total}</div>
                </div>
                <button
                    className="bg-orange-500 text-white px-6 py-2 rounded"
                    onClick={() => navigate('/')}
                >
                    Back to Home
                </button>
            </div>
        );
    }
    return null;
};

// Simple stepper component at the top
function Stepper({ step }) {
    return (
        <div className="flex justify-between mb-8">
            {steps.map((label, idx) => (
                <div key={label} className="flex-1 flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold 
            ${idx <= step ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {idx + 1}
                    </div>
                    <div className={`text-xs mt-1 ${idx === step ? 'font-semibold text-orange-500' : ''}`}>{label}</div>
                </div>
            ))}
        </div>
    );
}

export default Checkout;
